/* Home.jsx — Hero, mission, upcoming events, gallery teaser, newsletter */

function HomePage({ setRoute }) {
  const goTo = (id) => (e) => {
    e.preventDefault();
    setRoute(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div>
      <Hero setRoute={setRoute} />
      <Marquee items={[
        pprCurrentSeason(),
        "6 Camp Sites Across Philly",
        "Ages 6–18",
        "Free + Subsidized",
        "Dance · Theatre · Voice"
      ]} />
      <MissionBlock />
      <UpcomingEvents />
      <AthleticDanceBand />
      <WonderlandBand />
      <ProgramsTeaser setRoute={setRoute} />
      <GetInvolvedRow setRoute={setRoute} />
      <NewsletterStrip />
    </div>
  );
}

/* THE HERO IS THE DELL FINALE, FULL BLEED, AND THE VIDEO IS THE FIRST THING
   ANYBODY SEES. Chosen off three built options on 2026-09-06 (the other two
   were a video card in the right column and a clip bleeding off the right
   edge); this is the one that puts the footage above everything else.

   It replaced a four-photo Wonderland collage -- a 2:1 curtain call, an arc-set
   title card, a small inset and a tall portrait -- which is why WONDERLAND_TITLE
   and arcAt are gone from this file. Wonderland did not lose its place on the
   page: it is the band under the Athletic finale now, with its own 700 photos
   and its own link.

   THE CLIP IS "START OF THE SHOW" AND THE CHOICE WAS ABOUT LEGIBILITY, NOT
   TASTE. It is a drone move over the lit THE DELL MUSIC CENTER sign with the
   bowl and the arriving crowd behind it, so a first-time visitor knows what
   they are looking at before they read a word. The alternatives were the
   sunset aerial (prettiest, but unreadable as a place), the house filling up,
   and the river approach the old bottom banner used.

   IT IS ALSO THE HEAVIEST OF THE FIVE AT 7.1 MB, and it now sits at the top of
   the page rather than four screens down. That is the whole cost of this
   layout, and the reason every guard below exists rather than being copied out
   of habit:
     - poster first. dell-finale-event-02-poster.jpg is a still of the same
       frame, so the hero is complete and on-brand before a byte of video
       arrives, and IS the hero for anyone who never gets the rest.
     - preload="none" until the element is actually on screen. It is on screen
       immediately here, so this buys little on a laptop -- it is for the phone
       that lands on an anchor further down.
     - no autoplay at all for prefers-reduced-motion or Save-Data. Those
       visitors get the poster, permanently, and lose nothing else.
     - paused when scrolled away, which matters more here than it did at the
       bottom of the page: this clip is above everything, so without it the
       decode would run for the whole visit.

   THE SCRIM IS TWO GRADIENTS, NOT ONE. A left-to-right pass carries the
   headline column and a top-to-bottom pass keeps the sticky header legible at
   the top and the stats row legible at the bottom. A single flat overlay dark
   enough for white type at 104px would have muted the footage into a grey
   rectangle, which is the one thing this layout cannot afford. */
function Hero({ setRoute }) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const mm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)");
    const conn = navigator.connection || {};
    if ((mm && mm.matches) || conn.saveData) return;
    if (typeof IntersectionObserver === "undefined") return;

    var started = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          if (!started) {
            started = true;
            el.preload = "auto";
            el.load();
          }
          var p = el.play();
          if (p && p.catch) p.catch(() => {});
        } else {
          el.pause();
        }
      });
    }, { threshold: 0.25 });

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      className="hero-film"
      style={{
        position: "relative",
        overflow: "hidden",
        background: "var(--c-ink)",
        borderBottom: "3px solid var(--c-ink)",
        display: "flex",
        alignItems: "center",
        /* Not 100vh. A full-height hero on a laptop pushes the marquee and
           everything after it entirely below the fold, and the page stops
           looking like it has anything under the video. 86 leaves a band of
           the next section showing, which is what tells a visitor to scroll. */
        minHeight: "86vh",
      }}
    >
      <video
        ref={ref}
        muted
        loop
        playsInline
        preload="none"
        aria-hidden="true"
        tabIndex={-1}
        poster="/img/dell-finale-hero-v3-poster.jpg"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
      >
        <source src="/img/dell-finale-hero-v3.mp4" type="video/mp4" />
      </video>

      <div style={{
        position: "absolute", inset: 0,
        background:
          "linear-gradient(90deg, rgba(42,24,32,0.93) 0%, rgba(42,24,32,0.72) 42%, rgba(42,24,32,0.30) 100%)," +
          "linear-gradient(180deg, rgba(42,24,32,0.55) 0%, rgba(42,24,32,0.10) 40%, rgba(42,24,32,0.72) 100%)",
      }} />

      {/* width:100% said out loud: this is a flex item, and without it the
          container inside collapses to its own text and the headline hugs the
          left edge of the window instead of the page's column. Same note the
          other two bands carry. */}
      <div style={{ position: "relative", zIndex: 2, width: "100%", padding: "64px 0", color: "var(--c-cream)" }}>
        <div className="container">
          <div className="eyebrow" style={{ color: "var(--c-accent)" }}>
            08 · 10 · 2026 · The Dell Music Center
          </div>

          {/* Same clamp the cream hero used. It was tuned so four lines at
              0.95 do not push the stats row under the fold, and that
              constraint did not change when the ground behind it did. */}
          <h1
            className="display"
            style={{
              fontSize: "clamp(48px, 6.8vw, 104px)",
              margin: "16px 0 24px",
              color: "var(--c-cream)",
            }}
          >
            Where<br />
            <span style={{ color: "var(--c-accent)" }}>Philly kids</span><br />
            find their<br />
            <span style={{ fontStyle: "italic", fontWeight: 700 }}>spotlight.</span>
          </h1>

          {/* "Philly kids" is ochre here, not terracotta. Terracotta on this
              scrim sits a shade off the primary button directly below it and
              reads as a near-miss rather than a pair; ochre is the accent the
              other two bands already use for type on --c-ink. */}
          <p
            style={{
              fontSize: 19,
              lineHeight: 1.5,
              // 520 was the cream hero's width, where this sat in a half-width
              // column. Full bleed has the room, and at 520 the sentence broke
              // as "for ages 6 to / 18." -- a number stranded on its own line
              // under a 104px headline is the first thing the eye finds.
              maxWidth: 580,
              color: "rgba(251,243,226,0.84)",
              marginBottom: 32,
            }}
          >
            Performing arts summer camps, after-school programs, and showcases
            — across all neighborhoods, for ages 6 to 18.
          </p>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
            {/* Retires with the section it scrolls to. Left in place it would
                still be the hero's primary button, and would silently do
                nothing -- the optional chaining below swallows the miss. */}
            {liveEvents().length > 0 && (
              <a
                href="#upcoming-events"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("upcoming-events")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="btn"
              >
                Upcoming Events →
              </a>
            )}
            {/* Promotes itself to the primary button once the events go, so
                the hero is never left without one. */}
            <a href="#programs" onClick={(e) => { e.preventDefault(); setRoute("programs"); window.scrollTo(0,0); }}
               className={liveEvents().length > 0 ? "btn secondary" : "btn"}>
              Find a program near me
            </a>
            {/* The route into the footage playing behind it. Plain href with
                the query: setRoute never touches the hash, so "?set=dell"
                would be dropped and this would land on All photos. */}
            <a href="#gallery?set=dell" className="btn ochre">
              See the Dell Finale →
            </a>
          </div>

          <div style={{ display: "flex", gap: 36, marginTop: 40, flexWrap: "wrap" }}>
            <Stat n="6" label="Camp sites" light />
            <Stat n="50+" label="Year-round programs" light />
            <Stat n="600+" label="Young artists / yr" light />
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          /* vh on a phone is the browser's own moving target -- with the URL
             bar showing, 86vh of hero plus a 76px sticky header is the entire
             screen and nothing signals that the page continues. A fixed floor
             is steadier than a percentage here. */
          .hero-film { min-height: 620px; }
        }
      `}</style>
    </section>
  );
}

// `light` is for the stats sitting on the hero video. Terracotta on ink is
// the one pairing in this palette that goes muddy rather than merely dark, so
// the number takes the ochre the bands already use for type on --c-ink.
function Stat({ n, label, light }) {
  return (
    <div>
      <div className="display" style={{ fontSize: 48, lineHeight: 1, color: light ? "var(--c-accent)" : "var(--c-primary)" }}>{n}</div>
      <div style={{
        fontFamily: "var(--f-mono)",
        fontSize: 11,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: light ? "rgba(251,243,226,0.66)" : "var(--c-ink-soft)",
        marginTop: 6,
      }}>{label}</div>
    </div>
  );
}

function MissionBlock() {
  return (
    <section className="section" style={{ background: "var(--c-paper)", borderTop: "2px solid var(--c-ink)", borderBottom: "2px solid var(--c-ink)" }}>
      <div className="container">
        <div style={{ display: "grid", gridTemplateColumns: "0.4fr 1fr", gap: 56 }} className="mission-grid">
          <div>
            <div className="eyebrow">Our mission</div>
            <div className="display" style={{ fontSize: 56, marginTop: 12, lineHeight: 0.95 }}>
              The arts,<br />for every<br />Philadelphian.
            </div>
          </div>
          <div>
            <p style={{ fontSize: 22, lineHeight: 1.45, color: "var(--c-ink)" }}>
              Our mission is to cultivate vibrant, inclusive performing arts experiences
              that <span style={{ background: "var(--c-accent)", padding: "0 6px" }}>reflect and celebrate the diverse voices</span> of
              Philadelphia&apos;s communities. Through accessible programming, creative
              collaboration, and high-quality artistic opportunities, we empower
              individuals of all ages to explore their creativity, build confidence,
              and connect with one another.
            </p>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--c-ink-soft)", marginTop: 18 }}>
              By supporting local artists, nurturing emerging talent, and activating
              neighborhood spaces, we strengthen the social fabric of our city and
              ensure the arts remain a vital, equitable resource for all Philadelphians.
            </p>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 880px) {
          .mission-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
        }
      `}</style>
    </section>
  );
}

// CR #362 (July 2026): replaced the pre-registration camp cards with JJ's
// "Upcoming Events" flyer once camp went into session. Event list mirrors the
// flyer so screen readers and search engines get the same info as the graphic.
// `venue` is the Google Maps search query, not display copy. Name + street
// address so Maps resolves the exact building instead of a nearby match.
// `endsAt` is midnight AFTER the event, not its curtain time -- someone checking
// the site the morning of a show, or straight after it, should still see it. An
// event drops off the list on its own once that passes, and when the last one
// does the whole section stops rendering (see UpcomingEvents). Nothing here
// needs pulling down by hand.
// `phrase` exists because the section's subtitle used to name all four events in
// hand-written prose. Filtering the cards without filtering that sentence would
// leave the page advertising a competition that happened in July.
const UPCOMING_EVENTS = [
  { date: "July 29",     name: "Fire Prevention Play Competition",                     detail: "Free Library, Vine Street · 10:30AM–12:30PM",            venue: "Parkway Central Library, 1901 Vine St, Philadelphia, PA 19103",           endsAt: "2026-07-30T00:00:00-04:00", phrase: "the Fire Prevention Play Competition at the Free Library on Vine Street" },
  { date: "August 6–7",  name: "Young Performers Theatre Camp presents Wonderland",    detail: "Venice Island Performing Arts Center · Showtimes at 6:30PM", venue: "Venice Island Performing Arts Center, 7 Lock St, Philadelphia, PA 19127", endsAt: "2026-08-08T00:00:00-04:00", phrase: "the Young Performers Theatre Camp production of Wonderland at Venice Island" },
  { date: "August 10",   name: "Summer Camp Showcase",                                 detail: "The Dell Music Center · Showtime at 6:30PM",             venue: "The Dell Music Center, 2400 Strawberry Mansion Dr, Philadelphia, PA 19132", endsAt: "2026-08-11T00:00:00-04:00", phrase: "the citywide Summer Camp Showcase at the Dell Music Center" },
  { date: "August 13",   name: "Athletic Dance Finale",                                detail: "Athletic Recreation Center · Showtime at 6PM",           venue: "Athletic Recreation Center, 1400 N 26th St, Philadelphia, PA 19121",      endsAt: "2026-08-14T00:00:00-04:00", phrase: "the Athletic Dance Finale at Athletic Recreation Center" },
];

// Shared with the hero, which has an "Upcoming Events →" button that scrolls
// here. Once the last event passes, that button would scroll to a section that
// no longer exists -- so both read this one function rather than each deciding
// for itself.
function liveEvents(now = new Date()) {
  return UPCOMING_EVENTS.filter((ev) => now < new Date(ev.endsAt));
}
window.liveEvents = liveEvents;

// "A", "A and B", "A, B and C" -- the subtitle is a sentence, so it needs real
// conjunctions rather than a comma-joined list.
function joinPhrases(list) {
  if (list.length <= 1) return list[0] || "";
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

const mapsUrl = (venue) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`;

function UpcomingEvents() {
  const events = liveEvents();

  // Every event is done: the section retires itself rather than sitting on the
  // home page under "Here's what's next" with nothing next in it.
  if (events.length === 0) return null;

  return (
    <section className="section" id="upcoming-events" style={{ scrollMarginTop: 96 }}>
      <div className="container">
        <SectionHeader
          eyebrow="Summer 2026"
          title="Camp is on. Here's what's next."
          subtitle={`Summer camp is in full swing, and our young performers are getting ready to take the stage. Catch them at ${joinPhrases(events.map((ev) => ev.phrase))}.`}
        />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "start" }} className="events-grid">
          <div style={{ display: "grid", gap: 16 }}>
            {events.map((ev) => (
              <a
                key={ev.name}
                href={mapsUrl(ev.venue)}
                target="_blank"
                rel="noopener noreferrer"
                className="card event-card"
                aria-label={`${ev.name}, ${ev.date}. Open ${ev.venue} in Google Maps.`}
                style={{ display: "flex", gap: 18, alignItems: "baseline", padding: 22, textDecoration: "none", color: "inherit" }}
              >
                <div className="display" style={{ fontSize: 22, color: "var(--c-primary)", whiteSpace: "nowrap" }}>{ev.date}</div>
                <div>
                  <div className="display" style={{ fontSize: 21, lineHeight: 1.05 }}>{ev.name}</div>
                  <div style={{ fontFamily: "var(--f-mono)", fontSize: 12, letterSpacing: "0.06em", color: "var(--c-ink-soft)", marginTop: 8 }}>
                    {ev.detail}
                  </div>
                  <div className="event-map-cue" style={{ fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: "0.06em", color: "var(--c-primary)", marginTop: 8 }}>
                    View on map ↗
                  </div>
                </div>
              </a>
            ))}
          </div>

          <div style={{ borderRadius: 22, overflow: "hidden", border: "2px solid var(--c-ink)", boxShadow: "var(--shadow-lift)" }}>
            <img
              src="/img/upcoming-events-summer-2026.jpg"
              alt="Upcoming events flyer: Fire Prevention Play Competition, July 29 at the Free Library Vine Street, 10:30AM–12:30PM; Young Performers Theatre Camp presents Wonderland, August 6–7 at Venice Island Performing Arts Center, 6:30PM; Summer Camp Showcase, August 10 at the Dell Music Center, 6:30PM; Athletic Dance Finale, August 13 at Athletic Recreation Center, 6PM"
              loading="lazy"
              style={{ width: "100%", display: "block" }}
            />
          </div>
        </div>

        <style>{`
          @media (max-width: 880px) { .events-grid { grid-template-columns: 1fr !important; gap: 24px !important; } }
          .event-card { transition: transform 120ms ease, box-shadow 120ms ease; }
          .event-card:hover, .event-card:focus-visible { transform: translateY(-3px); box-shadow: 9px 9px 0 var(--c-ink); }
          .event-card:focus-visible { outline: 2px solid var(--c-primary); outline-offset: 3px; }
          @media (prefers-reduced-motion: reduce) { .event-card { transition: none; } .event-card:hover, .event-card:focus-visible { transform: none; } }
        `}</style>
      </div>
    </section>
  );
}

function ProgramsTeaser({ setRoute }) {
  const programs = [
    { type: "Dance",   count: 20, color: "#F5C6B0", icon: "💃" },
    { type: "Theater", count: 8,  color: "#F2D88F", icon: "🎭" },
    { type: "Music",   count: 6,  color: "#C9D9B0", icon: "🎤" },
  ];

  return (
    <section className="section" style={{ background: "var(--c-cream-deep)", borderTop: "2px solid var(--c-ink)", borderBottom: "2px solid var(--c-ink)" }}>
      <div className="container">
        <SectionHeader
          eyebrow="Year-round programs"
          title="Find your art form."
          subtitle="Performing arts rec center programs across the city. Filter by program type and find one walking distance from home."
        />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }} className="prog-grid">
          {programs.map((p) => (
            <a
              key={p.type}
              href="#programs"
              onClick={(e) => { e.preventDefault(); setRoute("programs"); window.scrollTo(0,0); }}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div className="card" style={{ background: p.color, transition: "transform 200ms" }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translate(-4px, -4px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translate(0, 0)"}
              >
                <div style={{ fontSize: 56, marginBottom: 16 }}>{p.icon}</div>
                <div className="display" style={{ fontSize: 32, marginBottom: 4 }}>{p.type}</div>
                <div style={{ fontFamily: "var(--f-mono)", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--c-ink-soft)" }}>
                  {p.count} active locations · find on map →
                </div>
              </div>
            </a>
          ))}
        </div>

        <style>{`
          @media (max-width: 880px) { .prog-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </div>
    </section>
  );
}

function BandStat({ n, label }) {
  return (
    <div>
      <div className="display" style={{ fontSize: 30, color: "var(--c-accent)", lineHeight: 1 }}>{n}</div>
      <div style={{ fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", marginTop: 4, opacity: 0.75 }}>{label}</div>
    </div>
  );
}

/* Athletic's own dance finale, 8/13/26, sitting directly under the Dell band as
   its sibling. The two are deliberately the same shape -- full-bleed picture,
   left-weighted scrim, eyebrow / headline / paragraph / stats / one button --
   because they are the same kind of thing: a season-ending night, and the only
   route on this page into that night's album.

   IT IS A STILL, NOT A CLIP, AND THAT IS NOT AN OVERSIGHT. Nothing was filmed
   at Athletic that evening. The Dell band's video machinery (IntersectionObserver,
   preload="none", the reduced-motion and saveData opt-outs) exists to make an
   autoplaying background cheap and polite; with no footage there is nothing for
   it to do, and copying it here would be inert code guarding a file that does
   not exist. An <img> is the whole implementation.

   WHY THIS FRAME. camp-show-athletic-175.jpg is the only kind of frame the
   layout accepts: its left third is empty dark wall, so the headline sits on
   nothing and the subject stays uncovered. Same constraint the Dell band
   records for its clip, and the same trap -- most of the good frames from this
   set (062, 105, 107) have a child exactly where the type goes. If you swap
   this file, check the left third first. camp-show-athletic-119.jpg is the
   other frame that passes.
   It also earns it on content: a camper in the summer camp tee, her medal, and
   the rest of the camp seated behind her. A photograph of the night AND of who
   it was for.

   THE SEVEN YEARS IS A CLAIM ABOUT A CITY PROGRAM, not a detail of the photo
   set. It came from Elliott and was confirmed for print on 2026-08-24. If PPR
   ever contradicts it, this is the copy that has to change -- not a number in a
   caption somewhere, the sentence below. Treat it like the urgency SLAs: a
   figure published on a phila.gov-facing page is a commitment, and nobody here
   can re-derive it from the repo.

   SCOPE CORRECTED 2026-08-25, on Elliott's instruction. The copy said "Seven
   years since Athletic Recreation Center last ran a dance summer camp", which
   read the claim as being about one facility. It is not, and never was -- see
   the paragraph above. It is DEPARTMENT-WIDE: the first Philadelphia Parks &
   Recreation dance summer camp anywhere in the city in seven years, which
   happened to be held at Athletic.
   That is a materially bigger claim than the one it replaced, and it is
   published on a phila.gov-facing page. It rests on Elliott's word, not on
   anything checkable here. If it is ever challenged, this is the sentence. */
function AthleticDanceBand() {
  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        background: "var(--c-ink)",
        borderBottom: "2px solid var(--c-ink)",
        display: "flex",
        alignItems: "center",
        minHeight: 460,
      }}
      className="athletic-band"
    >
      {/* Decorative: everything it shows is said in the copy beside it, so an
          alt here would be read out twice. Same call the Dell band makes with
          aria-hidden on its video. */}
      <img
        src="/img/camp-show-athletic-175.jpg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
      />

      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(90deg, rgba(42,24,32,0.93) 0%, rgba(42,24,32,0.66) 45%, rgba(42,24,32,0.16) 100%)",
      }} />

      <div style={{ position: "relative", zIndex: 2, width: "100%", padding: "56px 0", color: "var(--c-cream)" }}>
        <div className="container">
          <div className="eyebrow" style={{ color: "var(--c-accent)" }}>08 · 13 · 2026 · Athletic Recreation Center</div>
          <h2 className="display" style={{
            fontSize: "clamp(32px, 4.6vw, 62px)",
            lineHeight: 1,
            margin: "14px 0 18px",
            maxWidth: "15ch",
            color: "var(--c-cream)",
          }}>
            Dance came back to Philly Parks.
          </h2>
          <p style={{ fontSize: 17.5, lineHeight: 1.55, maxWidth: 560, color: "rgba(251,243,226,0.86)", marginBottom: 26 }}>
            Seven years since Philadelphia Parks &amp; Recreation ran a dance
            summer camp anywhere in the city &mdash; and it came back at
            Athletic Recreation Center with an evening of its own. Ballet,
            a neon number, colour and skirts, a butterfly solo, and a curtain
            call with a medal for every camper.
          </p>

          {/* FORWARD-LOOKING PROGRAMME COPY. THREE CLAIMS, NONE OF THEM
              DERIVABLE FROM THIS REPO. It was drafted by Claude on 2026-08-24
              and Elliott reviewed it the same day: he struck the longer-season
              claim ("camp will stay the same length") and cleared the rest to
              publish. What now stands, on his say-so and not on any source
              here, is that dance returns to the Intro Camp line-up next summer,
              that there are more places, and that registration opens in the
              spring.
              THE LENGTH OF THE CAMP IS THE ONE THING WE KNOW IS UNCHANGED. Do
              not let "more places" drift back into "longer" or "more weeks" in
              a later edit -- that exact sentence was already wrong once.
              This is a promise about a City of Philadelphia programme on the
              page parents read to decide where to send a child, which puts it
              in the same class as the urgency SLAs and the opening hours: if
              PPR ever says different, this copy changes, and nobody working
              here can check it from the code. Ask J.J., not the repo. */}
          <p style={{ fontSize: 17.5, lineHeight: 1.55, maxWidth: 560, color: "rgba(251,243,226,0.86)", marginBottom: 26 }}>
            And it is not a one-off. Dance returns to the Intro Camp line-up next
            summer with more places, and registration opens in the spring.
          </p>

          <div style={{ display: "flex", gap: 30, flexWrap: "wrap", marginBottom: 28 }}>
            <BandStat n="7" label="Years since PPR's last one" />
            <BandStat n="182" label="Photos" />
          </div>

          {/* Plain href carrying the query, for the reason the Dell band's link
              spells out: setRoute never touches the hash, so the ?set= and the
              &loc= would be dropped and this would land on All photos. */}
          <a href="#gallery?set=introcamps&loc=athletic" className="btn">
            See the Athletic finale &rarr;
          </a>
        </div>
      </div>

      <style>{`
        @media (max-width: 700px) {
          .athletic-band { min-height: 380px; }
        }
      `}</style>
    </section>
  );
}

/* Wonderland at Venice Island, 8/6-7/26, as a still band in the Athletic
   band's shape. It replaced the five-photo teaser strip that used to sit
   down where the Dell band now is: the strip was the page's route into this
   album, and a band says what the night was as well as showing it.

   IT SITS DIRECTLY BELOW ATHLETIC, WHICH IS WHY IT CARRIES THE SEAM MARGIN.
   Two full-bleed --c-ink bands in a row read as one 900px slab of dark with
   a join in the middle; the margin lets the page ground show between them.
   That margin used to live on Athletic, when the Dell band was above it.

   WHY THIS FRAME. night2-145 is the Mad Hatter's tea party: the whole company
   ranged along the table on a rainbow backdrop, most of them playing straight
   to the camera. It replaced night2-258, a single Hatter pointing across an
   empty stage, which satisfied the empty-left-third rule the other bands
   follow and was the duller picture for it -- J.J. asked for something funnier
   on 2026-09-06 and this is the funniest frame in 700.

   It breaks that rule on purpose, and the scrim below is what pays for it.
   See the note there before swapping this file: a frame this wide needs the
   steep gradient, and a frame with an empty left third would look wrong under
   it. The two choices go together.

   THE COUNT IS HARDCODED AND THE ALBUM IS NOT, the same way the Dell band's
   is. 700 is every photo tagged wonderland -- 325 from each night plus 50
   from the rehearsal -- and it silently stops matching the moment photos are
   added. Re-derive before trusting it:

     node -e "const m=require('./img/manifest.json');
       console.log(m.items.filter(i=>i.cat==='wonderland').length)"

   The two nights are 6 and 7 August, the dates the events list above already
   publishes. Nothing here claims anything the repo cannot show. */
function WonderlandBand() {
  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        background: "var(--c-ink)",
        borderTop: "2px solid var(--c-ink)",
        borderBottom: "2px solid var(--c-ink)",
        display: "flex",
        alignItems: "center",
        minHeight: 460,
        marginTop: "clamp(14px, 2vw, 26px)",
      }}
      className="wonderland-band"
    >
      {/* Decorative: the copy beside it says everything it shows, so an alt
          here would be read out twice. Same call the other two bands make. */}
      <img
        src="/img/yptc-wonderland-night2-145.jpg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
      />

      {/* STEEPER THAN THE ATHLETIC BAND'S, and that is this photograph's
          doing. The company is spread right across the tea table from about a
          tenth of the frame to nine tenths, so there is no empty left third to
          put type on -- the trade the other bands avoid by choosing a frame
          with one. A slow gradient here would half-dim a dozen children and
          read as a mistake; this one is effectively a panel, dark to 38% and
          clear by 64%, so the type sits on ink and the rest of the cast keeps
          its colour. Covering four of them deliberately looks better than
          muddying twenty by accident. */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(90deg, rgba(42,24,32,0.95) 0%, rgba(42,24,32,0.92) 38%, rgba(42,24,32,0.30) 64%, rgba(42,24,32,0.12) 100%)",
      }} />

      <div style={{ position: "relative", zIndex: 2, width: "100%", padding: "56px 0", color: "var(--c-cream)" }}>
        <div className="container">
          <div className="eyebrow" style={{ color: "var(--c-accent)" }}>08 · 06–07 · 2026 · Venice Island</div>
          <h2 className="display" style={{
            fontSize: "clamp(32px, 4.6vw, 62px)",
            lineHeight: 1,
            margin: "14px 0 18px",
            maxWidth: "15ch",
            color: "var(--c-cream)",
          }}>
            Two nights in Wonderland.
          </h2>
          <p style={{ fontSize: 17.5, lineHeight: 1.55, maxWidth: 560, color: "rgba(251,243,226,0.86)", marginBottom: 26 }}>
            The Young Performers Theatre Camp &mdash; the teen company at Venice
            Island &mdash; closed its summer with a full production of
            Wonderland, played twice to a full house on 6 and 7 August. A Queen
            of Hearts, a Mad Hatter, a tea party, and a curtain call with the
            whole company on stage.
          </p>

          <div style={{ display: "flex", gap: 30, flexWrap: "wrap", marginBottom: 28 }}>
            <BandStat n="2" label="Nights" />
            <BandStat n="700" label="Photos" />
          </div>

          {/* Plain href carrying the query, for the reason the other two bands
              spell out: setRoute never touches the hash, so "?set=wonderland"
              would be dropped and this would land on All photos. */}
          <a href="#gallery?set=wonderland" className="btn">
            See the Wonderland gallery &rarr;
          </a>
        </div>
      </div>

      <style>{`
        @media (max-width: 700px) {
          .wonderland-band { min-height: 380px; }
        }
      `}</style>
    </section>
  );
}

function GetInvolvedRow({ setRoute }) {
  return (
    <section className="section-tight" style={{ background: "var(--c-ink)", color: "var(--c-cream)" }}>
      <div className="container">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }} className="involved-grid">
          <CTACard
            color="var(--c-primary)"
            label="For rec center leaders"
            title="Promote a program at your center."
            body="Submit your program details — name, location, contact — and our team will review and add it to the citywide map."
            cta="Promote a program →"
            onClick={() => { setRoute("promote"); window.scrollTo(0,0); }}
          />
          <CTACard
            color="var(--c-accent)"
            label="For working artists & teachers"
            title="Lead a workshop with us."
            body="Got a skill you'd love to share — improv, sound design, lighting, hip-hop choreo? Tell us about it."
            cta="Workshop development →"
            onClick={() => { setRoute("workshop"); window.scrollTo(0,0); }}
          />
        </div>
        <style>{`
          @media (max-width: 800px) {
            .involved-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    </section>
  );
}

function CTACard({ color, label, title, body, cta, onClick }) {
  return (
    <div
      className="card"
      style={{
        background: color,
        color: "var(--c-ink)",
        padding: 32,
      }}
    >
      <div className="eyebrow" style={{ marginBottom: 14 }}>{label}</div>
      <div className="display" style={{ fontSize: 36, marginBottom: 12, lineHeight: 1 }}>{title}</div>
      <p style={{ fontSize: 15, lineHeight: 1.55, marginBottom: 20, color: "var(--c-ink)" }}>{body}</p>
      <button onClick={onClick} className="btn secondary small">{cta}</button>
    </div>
  );
}

function NewsletterStrip() {
  const [email, setEmail] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/loop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "home_newsletter" })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || `error_${res.status}`);
      setSubmitted(true);
    } catch (err) {
      setError("Couldn't subscribe right now. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section" style={{ background: "var(--c-cream)" }}>
      <div className="container">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 40,
            alignItems: "center",
          }}
          className="news-grid"
        >
          <div>
            <div className="eyebrow">Stay in the loop</div>
            <div className="display" style={{ fontSize: 56, marginTop: 12, lineHeight: 0.95 }}>
              Showtime<br />updates,<br />once a month.
            </div>
          </div>
          <div>
            {!submitted ? (
              <form onSubmit={onSubmit} style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                  type="email"
                  required
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    minWidth: 220,
                    padding: "16px 18px",
                    border: "2px solid var(--c-ink)",
                    borderRadius: 999,
                    fontSize: 16,
                    background: "var(--c-paper)",
                    fontFamily: "var(--f-body)",
                  }}
                />
                <button type="submit" className="btn" disabled={submitting}>
                  {submitting ? "Subscribing…" : "Subscribe →"}
                </button>
              </form>
            ) : (
              <div className="card" style={{ background: "var(--c-tertiary)", color: "var(--c-paper)" }}>
                <div className="display" style={{ fontSize: 26, color: "var(--c-paper)" }}>You&apos;re on the list ✦</div>
                <div style={{ fontSize: 14, marginTop: 8, opacity: 0.85 }}>
                  Watch your inbox for showcases, audition calls, and camp news.
                </div>
              </div>
            )}
            {error && (
              <div style={{ marginTop: 12, fontSize: 13, color: "var(--c-primary-deep)" }}>{error}</div>
            )}
            <div style={{
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--c-ink-soft)",
              marginTop: 16,
            }}>
              Showcases · audition calls · new programs · no spam.
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 800px) {
            .news-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, subtitle, right }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 40, gap: 24, flexWrap: "wrap" }}>
      <div style={{ maxWidth: 720 }}>
        <div className="eyebrow">{eyebrow}</div>
        <div className="display" style={{ fontSize: "clamp(36px, 5.5vw, 64px)", marginTop: 12, lineHeight: 0.98 }}>{title}</div>
        {subtitle && (
          <p style={{ fontSize: 17, color: "var(--c-ink-soft)", marginTop: 14, maxWidth: 600 }}>{subtitle}</p>
        )}
      </div>
      {right}
    </div>
  );
}

Object.assign(window, { HomePage, SectionHeader });
